import type { ManufacturingCase } from '@infinitform/platform-core';
import { ManufacturingMethod } from '../models/enums';
import { computeCaseLabel } from './case-sync.util';
import { computeVariantLabel, makeDefaultVariant } from './variant.util';

/**
 * Extrusion methods are prismatic-only — `generatePrismatic` must be true or
 * the prismatic FEA pass is generated as a TO problem (wrong optimization
 * type + design domain). Cases saved before `captureActiveCaseFromFlat`
 * preserved the flag are missing it; the normalizer backfills on load so
 * existing projects heal on their next save.
 */
function isPrismaticOnly(method: unknown): boolean {
  return (
    method === ManufacturingMethod.Extrusion ||
    method === ManufacturingMethod.InjectionMoldingWithExtrusion
  );
}

/**
 * Loose shape of a `project.config` payload coming from the backend — includes
 * both the legacy flat fields (single-method projects) and the optional new
 * `manufacturingMethods` array. Matches what `GET /project/:id` returns today
 * (the backend's `config` field is loosely typed as an object blob).
 *
 * `manufacturingMethod` is declared loosely because very old project documents
 * stored it as an array of method-config objects with an `{id, type, ...}`
 * shape; the normalizer below coerces those back into real cases.
 */
export interface LegacyProjectConfigShape {
  manufacturingMethod?: string | LegacyMethodEntry[];
  millingVector?: {
    xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean;
  };
  drawDirectionVector?: {
    xp: boolean; yp: boolean; zp: boolean; xm: boolean; ym: boolean; zm: boolean;
  };
  generatePrismatic?: boolean;
  costModel?: {
    axesType: '3-axis' | '4-axis' | '5-axis';
    removalRate: number;
    machiningCostPerHour: number;
    setupCost: number;
  };
  minimumToolSize?: number;
  minimumToolSizeUnit?: 'mm' | 'in';
  ribsDensity?: 'Low' | 'Medium';
  wallThickness?: number | 'auto';
  bottomCutFrac?: number;
  skeletonMethod?: 'Skeleton3D' | 'Skeleton2D';
  maxMillingDepth?: ManufacturingCase['maxMillingDepth'];
  thinWeb?: ManufacturingCase['thinWeb'];
  castingParametric?: boolean;

  materials?: { id: string; name?: string }[];
  manufacturingMethods?: ManufacturingCase[];

  // Passthrough: anything else on config stays untouched.
  [key: string]: unknown;
}

/**
 * Old pre-multi-MF DB shape sometimes found in `config.manufacturingMethod`
 * (array form) or leaked into `manufacturingMethods[i].method` (array form).
 * Uses `id`/`type` instead of `methodId`/`method`.
 */
interface LegacyMethodEntry {
  id?: string;
  type?: string;
  millingVector?: LegacyProjectConfigShape['millingVector'];
  drawDirectionVector?: LegacyProjectConfigShape['drawDirectionVector'];
  minimumToolSize?: number;
  minimumToolSizeUnit?: 'mm' | 'in';
  generatePrismatic?: boolean;
  generateOrganic?: boolean;
  costModel?: LegacyProjectConfigShape['costModel'];
  ribsDensity?: 'Low' | 'Medium';
  wallThickness?: number | 'auto';
  bottomCutFrac?: number;
  skeletonMethod?: 'Skeleton3D' | 'Skeleton2D';
}

/**
 * RFC4122 v4-ish UUID generator. Kept small to avoid adding a dependency.
 * Callers that need a cryptographically stronger UUID can swap this later.
 */
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Convert one legacy `{id, type, ...}` entry into a valid `ManufacturingCase`.
 * Missing `type` falls back to Additive Manufacturing so the downstream code
 * always has a string method to branch on.
 */
function caseFromLegacyEntry(entry: LegacyMethodEntry): ManufacturingCase {
  const c: ManufacturingCase = {
    methodId: entry.id ?? uuidv4(),
    method: typeof entry.type === 'string' ? entry.type : 'Additive Manufacturing',
  };
  if (entry.millingVector !== undefined) c.millingVector = entry.millingVector;
  if (entry.drawDirectionVector !== undefined) c.drawDirectionVector = entry.drawDirectionVector;
  if (entry.generatePrismatic !== undefined) c.generatePrismatic = entry.generatePrismatic;
  if (entry.costModel !== undefined) c.costModel = entry.costModel;
  if (entry.minimumToolSize !== undefined) c.minimumToolSize = entry.minimumToolSize;
  if (entry.minimumToolSizeUnit !== undefined) c.minimumToolSizeUnit = entry.minimumToolSizeUnit;
  if (entry.ribsDensity !== undefined) c.ribsDensity = entry.ribsDensity;
  if (entry.wallThickness !== undefined) c.wallThickness = entry.wallThickness;
  if (entry.bottomCutFrac !== undefined) c.bottomCutFrac = entry.bottomCutFrac;
  if (entry.skeletonMethod !== undefined) c.skeletonMethod = entry.skeletonMethod;
  if (isPrismaticOnly(c.method)) c.generatePrismatic = true;
  return c;
}

/**
 * Repair a `manufacturingMethods` array whose entries were corrupted by an
 * earlier normalization against an array-shaped legacy `manufacturingMethod`
 * field (symptoms: `method` holds an array of `{id, type, ...}` objects and
 * `label` renders as `"[object Object],[object Object],..."`). If the
 * corruption is present, the legacy entries are extracted into proper cases;
 * otherwise the array is returned untouched.
 */
function repairCorruptedCases(cases: ManufacturingCase[]): ManufacturingCase[] {
  const corrupted = cases.some((c) => Array.isArray((c as { method: unknown }).method));
  if (!corrupted) return cases;

  const repaired: ManufacturingCase[] = [];
  for (const c of cases) {
    const raw = (c as { method: unknown }).method;
    if (Array.isArray(raw)) {
      for (const entry of raw as LegacyMethodEntry[]) {
        repaired.push(caseFromLegacyEntry(entry));
      }
    } else {
      repaired.push(c);
    }
  }
  return repaired;
}

/**
 * If the project's config already has a well-formed `manufacturingMethods[]`,
 * return as-is. Otherwise lift the legacy flat fields into the array. Handles
 * three legacy shapes:
 *
 *  1. `manufacturingMethod: <enum string>` (post-2024 single-method projects)
 *     → wrap into a single-element `manufacturingMethods[0]`.
 *  2. `manufacturingMethod: LegacyMethodEntry[]` (very old projects where the
 *     flat field stored an array of `{id, type, ...}` objects)
 *     → explode into N cases, one per entry.
 *  3. `manufacturingMethods[i].method: LegacyMethodEntry[]` (already-corrupted
 *     projects produced by a prior normalization against shape 2)
 *     → repair by extracting the array entries back into proper cases.
 *
 * The flat legacy fields are left in place (V2 mirror strategy), but
 * `manufacturingMethod` is coerced to a string enum drawn from the first case
 * so downstream XML writers / filters never see an array.
 *
 * Pure function: does not mutate the input. Returns a new object.
 */
function normalizeCases<T extends LegacyProjectConfigShape>(config: T): T {
  // If `manufacturingMethods` is an explicit array — even an empty one — it is
  // the canonical source of truth. Do NOT fall through to the legacy
  // `manufacturingMethod` field, otherwise deleting the last case would
  // silently revive it from the (now stale) flat singular field on next load.
  if (Array.isArray(config.manufacturingMethods)) {
    if (config.manufacturingMethods.length === 0) {
      // Also clear the stale flat mirror — same trio that
      // `removeManufacturingCase` clears live when the last case is deleted.
      // Otherwise method-specific panels (Milling Direction, cost model, …)
      // that guard on `manufacturingMethod === <enum>` reappear on reload.
      return {
        ...config,
        manufacturingMethods: [],
        manufacturingMethod: undefined,
        millingVector: undefined,
        drawDirectionVector: undefined,
      };
    }
    const repaired = repairCorruptedCases(config.manufacturingMethods);

    // Backfill `generatePrismatic` on extrusion cases (see isPrismaticOnly).
    let prismaticBackfilled = false;
    const backfilled = repaired.map((c) => {
      if (isPrismaticOnly(c.method) && c.generatePrismatic !== true) {
        prismaticBackfilled = true;
        return { ...c, generatePrismatic: true };
      }
      return c;
    });

    // Case labels are derived strings (computeCaseLabel is their only
    // writer). Persisted copies go stale when a method's display name
    // changes (e.g. "Die Casting" → "Casting"), and they propagate into
    // children summaries and completion emails — refresh them on load.
    let labelsChanged = false;
    const relabeled = backfilled.map((c) => {
      const label = computeCaseLabel(c);
      if (c.label === label) return c;
      labelsChanged = true;
      return { ...c, label };
    });

    if (repaired === config.manufacturingMethods && !prismaticBackfilled && !labelsChanged) return config;
    const cases = labelsChanged ? relabeled : backfilled;
    return {
      ...config,
      manufacturingMethods: cases,
      manufacturingMethod: cases[0].method,
    };
  }

  const legacy = config.manufacturingMethod;

  if (Array.isArray(legacy)) {
    const cases = legacy.map((entry) => caseFromLegacyEntry(entry));
    return {
      ...config,
      manufacturingMethods: cases,
      manufacturingMethod: cases[0]?.method ?? 'Additive Manufacturing',
    };
  }

  // Legacy single-method projects: lift the flat field into a one-case array.
  if (typeof legacy === 'string') {
    const lifted: ManufacturingCase = {
      methodId: uuidv4(),
      method: legacy,
    };

    if (config.millingVector !== undefined) lifted.millingVector = config.millingVector;
    if (config.drawDirectionVector !== undefined) lifted.drawDirectionVector = config.drawDirectionVector;
    if (config.generatePrismatic !== undefined) lifted.generatePrismatic = config.generatePrismatic;
    if (config.costModel !== undefined) lifted.costModel = config.costModel;
    if (config.minimumToolSize !== undefined) lifted.minimumToolSize = config.minimumToolSize;
    if (config.minimumToolSizeUnit !== undefined) lifted.minimumToolSizeUnit = config.minimumToolSizeUnit;
    if (config.ribsDensity !== undefined) lifted.ribsDensity = config.ribsDensity;
    if (config.wallThickness !== undefined) lifted.wallThickness = config.wallThickness;
    if (config.bottomCutFrac !== undefined) lifted.bottomCutFrac = config.bottomCutFrac;
    if (config.skeletonMethod !== undefined) lifted.skeletonMethod = config.skeletonMethod;
    if (config.maxMillingDepth !== undefined) lifted.maxMillingDepth = config.maxMillingDepth;
    if (config.thinWeb !== undefined) lifted.thinWeb = config.thinWeb;
    if (config.castingParametric !== undefined) lifted.castingParametric = config.castingParametric;
    if (isPrismaticOnly(lifted.method)) lifted.generatePrismatic = true;

    return {
      ...config,
      manufacturingMethods: [lifted],
    };
  }

  // Brand-new project with no method specified — start empty.
  // The user must pick a manufacturing method before optimization is enabled
  // (see canOptimize in features/flow/optimization/utils/validation.util.ts).
  return {
    ...config,
    manufacturingMethods: [],
  };
}

/**
 * Ensure every case has at least one valid variant.
 *
 * A case with no variants gets one reusing its own `methodId` as the
 * variantId — existing child projects are linked by `child.methodId`, so
 * reusing it keeps already-run masters attached to their results without a
 * document migration.
 *
 * Variants pointing at a material that has since left the catalog are
 * dropped; a case emptied that way is re-seeded onto `materials[0]`.
 * Labels are derived strings, so they are recomputed here on every load.
 */
function backfillVariants<T extends LegacyProjectConfigShape>(
  config: T,
  catalogIsAuthoritative = false,
): T {
  const cases = config.manufacturingMethods;
  const materials = config.materials ?? [];
  if (!Array.isArray(cases) || cases.length === 0) return config;
  // On load an empty catalog means "not known yet", so leaving variants alone
  // is the safe read. When the caller owns the catalog it means "genuinely
  // empty", and every variant referencing it is an orphan.
  if (materials.length === 0 && !catalogIsAuthoritative) return config;

  const nameById = new Map(materials.map((m) => [m.id, m.name ?? '']));
  let changed = false;

  const next = cases.map((c) => {
    const kept = (c.variants ?? []).filter((v) => nameById.has(v.materialId));
    const seeded =
      kept.length > 0 || materials.length === 0
        ? kept
        : [makeDefaultVariant(c.methodId, materials[0].id)];

    const relabelled = seeded.map((v) => {
      const label = computeVariantLabel(c, nameById.get(v.materialId) ?? '');
      return v.label === label ? v : { ...v, label };
    });

    const same =
      c.variants !== undefined &&
      relabelled.length === c.variants.length &&
      relabelled.every((v, i) => v === c.variants![i]);

    if (same) return c;
    changed = true;
    return { ...c, variants: relabelled };
  });

  return changed ? { ...config, manufacturingMethods: next } : config;
}

/**
 * Migrate a project config to the current shape: legacy manufacturing fields
 * are lifted into `manufacturingMethods[]`, then every case is guaranteed at
 * least one material variant.
 */
export function normalizeToNewShape<T extends LegacyProjectConfigShape>(config: T): T {
  return retireWebThicknessLimits(backfillVariants(normalizeCases(config)));
}

type MaxMillingDepth = ManufacturingCase['maxMillingDepth'];

/** A max milling depth saved with the retired 'webThickness' limit, turned off and back to a depth limit. */
function retiredWebThickness(settings: MaxMillingDepth): MaxMillingDepth {
  if (settings?.limitBy !== 'webThickness') return settings;
  const { limitBy: _retired, ...rest } = settings;
  return { ...rest, enabled: false };
}

/**
 * The min web thickness limit (`maxMillingDepth.limitBy: 'webThickness'`) is
 * retired: a project saved with it opens with the milling limit off. The
 * value and overrides are kept, so turning the limit on uses them as depths.
 */
function retireWebThicknessLimits<T extends LegacyProjectConfigShape>(config: T): T {
  const flat = retiredWebThickness(config.maxMillingDepth);
  let casesChanged = false;
  const cases = config.manufacturingMethods?.map((c) => {
    const maxMillingDepth = retiredWebThickness(c.maxMillingDepth);
    if (maxMillingDepth === c.maxMillingDepth) return c;
    casesChanged = true;
    return { ...c, maxMillingDepth };
  });
  if (flat === config.maxMillingDepth && !casesChanged) return config;
  return {
    ...config,
    maxMillingDepth: flat,
    ...(casesChanged ? { manufacturingMethods: cases } : {}),
  };
}

/**
 * The two fields variant reconciliation reads. Narrower than
 * `LegacyProjectConfigShape` so a live editor `Project` satisfies it.
 */
export interface VariantCatalogHost {
  materials?: { id: string; name?: string }[];
  manufacturingMethods?: ManufacturingCase[];
}

/**
 * Reconcile variants against a catalog the caller has just changed.
 *
 * Same rules as the load-time backfill, with one difference: an empty catalog
 * is taken at face value rather than as "not loaded yet", so clearing every
 * material leaves no variants behind instead of a list of unresolvable ones.
 *
 * Mutates `config.manufacturingMethods` in place, because the callers hold a
 * live project object rather than a config they are about to reassign.
 */
export function reconcileVariantsWithCatalog(host: VariantCatalogHost): void {
  const shape = host as unknown as LegacyProjectConfigShape;
  const reconciled = backfillVariants(shape, true);
  if (reconciled !== shape) {
    host.manufacturingMethods = reconciled.manufacturingMethods;
  }
}
